import type { MetaDescriptor, MetaFunction } from 'react-router'
import { isRouteErrorResponse } from 'react-router'
import { CANONICAL_HOSTNAME, SITE_TITLE } from '~/config'

const SITE_URL = `https://${CANONICAL_HOSTNAME}`
const base = [
  /**
   * Must render as `<meta charSet>` (not `name="charset"`, which is inert) and
   * must stay first: the HTML spec requires the encoding declaration inside the
   * first 1024 bytes. If the browser only discovers it later it restarts the
   * parse, painting one unstyled frame before the stylesheets apply.
   */
  { charSet: 'utf-8' },
  { name: 'viewport', content: 'width=device-width,initial-scale=1' },
  {
    name: 'theme-color',
    content: 'hsl(342deg 100% 67%)',
    media: '(prefers-color-scheme: light)',
  },
  {
    name: 'theme-color',
    content: 'hsl(340deg 95% 55%)',
    media: '(prefers-color-scheme: dark)',
  },
]

type Matches = Parameters<MetaFunction>[0]['matches']

// Route loaders may pass these directly; otherwise they come from the root.
const fromRoot = (
  data: Record<string, unknown>,
  matches: Matches,
  key: 'requestOrigin',
): string | undefined => {
  const own = data[key]
  if (typeof own === 'string') return own

  const rootData = matches.find((match) => match.id === 'root')?.loaderData
  if (rootData && typeof rootData === 'object' && key in rootData) {
    const value = (rootData as Record<string, unknown>)[key]
    if (typeof value === 'string') return value
  }

  return undefined
}

const getRequestOrigin = (
  data: Record<string, unknown>,
  matches: Matches,
): string => fromRoot(data, matches, 'requestOrigin') ?? SITE_URL

/**
 * Always the production host, never `requestOrigin`: a deploy preview must not
 * canonicalise to itself. Trailing slashes collapse so `/apps/` and `/apps`
 * count as one page.
 */
const selfCanonical = (path: string | undefined): string | undefined => {
  if (!path) return undefined
  const trimmed = path.length > 1 ? path.replace(/\/+$/, '') : path
  return `${SITE_URL}${trimmed}`
}

// Loaders hand us plain strings or `Person` records; joining the raw array
// shipped `content="[object Object]"` on every blog post.
const toAuthorNames = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return []

  return raw.flatMap((author) => {
    if (typeof author === 'string') return author.trim() ? [author.trim()] : []

    if (author && typeof author === 'object' && 'name' in author) {
      const { name } = author as { name?: unknown }
      if (typeof name === 'string' && name.trim()) return [name.trim()]
    }

    return []
  })
}

// The authored string, not the `Date` beside it: that carries a display shift,
// and Google requires structured and visible dates to match.
const toIsoDate = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.trim() ? raw.trim() : undefined

const organization = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_TITLE,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/images/omni-logo.webp`,
  },
  sameAs: ['https://x.com/exploreomni'],
}

const siteLdJson = (): MetaDescriptor => ({
  'script:ld+json': {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_TITLE,
        url: SITE_URL,
        publisher: { '@id': organization['@id'] },
      },
    ],
  },
})

export const meta: MetaFunction = ({
  loaderData: castData,
  matches,
  error,
  location,
}) => {
  /**
   * Distinct error titles let analytics segment broken links and crashes by
   * page title, which the plain site-title fallback made impossible.
   */
  if (error) {
    const title =
      isRouteErrorResponse(error) && error.status === 404
        ? `Page not found - ${SITE_TITLE}`
        : `Something went wrong - ${SITE_TITLE}`
    return [...base, { title }]
  }

  const data = (castData ?? {}) as Record<string, unknown>
  const requestOrigin = getRequestOrigin(data, matches)
  const authorNames = toAuthorNames(data.authors)
  const banner = (data.banner as undefined | Record<string, unknown>) ?? {
    src: '/images/unfurl-image-2025.png',
  }
  const swiftypeSiteType =
    'swiftypeSiteType' in data ? data.swiftypeSiteType : undefined

  const pageTitle = data.title ? `${data.title} - ${SITE_TITLE}` : SITE_TITLE
  const title = data.sectionTitle
    ? `${data.sectionTitle}: ${pageTitle}`
    : pageTitle
  const description = data?.description || data?.subtitle
  let image =
    typeof data.socialImage === 'string' && data.socialImage.trim()
      ? data.socialImage.trim()
      : banner.src

  if (typeof image === 'string') {
    // Contentful returns protocol-relative URLs: fine for og:image, invalid
    // as a schema.org URL.
    if (image.startsWith('//')) image = `https:${image}`
    /**
     * If image is locally hosted, prepend the current request origin so deploy
     * previews and staging URLs produce crawlable social card images.
     */ else if (!image.includes('//')) image = `${requestOrigin}${image}`
  }

  const noindex = 'noindex' in data ? Boolean((data as any).noindex) : false
  // Follow the route's indexing policy even when it supplies a canonical.
  const canonical = noindex
    ? undefined
    : typeof data.canonical === 'string'
      ? data.canonical
      : selfCanonical(location.pathname)

  const swiftype = swiftypeSiteType
    ? [
        {
          name: 'site-priority',
          class: 'swiftype',
          content: '1',
          'data-type': 'integer',
        },
        {
          name: 'site-type',
          class: 'swiftype',
          content: swiftypeSiteType,
          'data-type': 'string',
        },
      ]
    : []

  const isArticle = data.ogType === 'article'
  const datePublished = isArticle ? toIsoDate(data.publishedRaw) : undefined
  const dateModified = isArticle ? toIsoDate(data.modifiedRaw) : undefined

  const tags: ReturnType<MetaFunction> = [
    ...base,
    { title },
    { name: 'description', content: description },
    { name: 'keywords', content: data?.keywords },
    ...(authorNames.length
      ? [{ name: 'author', content: authorNames.join(', ') }]
      : []),
    ...swiftype,
    // No `{}` placeholders: <Meta> keys a bare descriptor on its JSON, so two
    // of them collide.
    ...(canonical
      ? [
          // Without tagName, <Meta> emits this as a <meta>, not a <link>.
          { tagName: 'link', rel: 'canonical', href: canonical },
          { property: 'og:url', content: canonical },
        ]
      : []),
    { property: 'og:type', content: isArticle ? 'article' : 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },

    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@exploreomni' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
  ]

  if (isArticle) {
    if (datePublished)
      tags.push({ property: 'article:published_time', content: datePublished })
    if (dateModified)
      tags.push({ property: 'article:modified_time', content: dateModified })
    authorNames.forEach((name) => {
      tags.push({ property: 'article:author', content: name })
    })

    tags.push(
      articleLdJson({
        authorNames,
        canonical,
        datePublished,
        dateModified,
        description,
        headline: typeof data.title === 'string' ? data.title : title,
        image,
      }),
    )
  }

  tags.push(siteLdJson())

  if (canonical && Array.isArray(data.breadcrumbs)) {
    tags.push({
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data.breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${SITE_URL}${item.path}`,
        })),
      },
    })
  }

  if (noindex) {
    tags.push({ name: 'robots', content: 'noindex, nofollow' })
  }

  return tags
}

/**
 * Google asks for one entry per author rather than a joined string: "When
 * specifying multiple authors, list each author in their own `author` field."
 */
const articleLdJson = ({
  authorNames,
  canonical,
  datePublished,
  dateModified,
  description,
  headline,
  image,
}: {
  authorNames: string[]
  canonical: string | undefined
  datePublished: string | undefined
  dateModified: string | undefined
  description: unknown
  headline: string
  image: unknown
}): MetaDescriptor => ({
  'script:ld+json': {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    ...(typeof description === 'string' ? { description } : {}),
    ...(typeof image === 'string' ? { image: [image] } : {}),
    ...(canonical
      ? {
          url: canonical,
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        }
      : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(authorNames.length
      ? {
          author: authorNames.map((name) => ({ '@type': 'Person', name })),
        }
      : {}),
    publisher: organization,
  },
})
